import type {
  AuthorizationRequest,
  AuthorizationResponse,
} from '@/shared/types/auth';
import type { User } from 'firebase/auth';

// TODO: timeouts via AbortController

const baseRequest: RequestInit = {
  keepalive: true,
};

interface ApiOptions {
  // Override Access-Control-Allow-Origin
  // Needed in SSR contexts due to no "window" object
  origin?: string;
  baseUrl: string;
}

async function generateApiRequestInit(
  user: User | null,
  init?: RequestInit,
  options?: ApiOptions,
) {
  const result: RequestInit = { ...(init || {}) };

  const headers = new Headers({
    'Access-Control-Allow-Origin': options?.origin ?? window.location.hostname,
    ...(result.headers || {}),
  });

  if (user !== null) {
    const token = await user.getIdToken();
    headers.set('Authorization', 'Bearer ' + token);
    headers.set('Access-Control-Allow-Headers', 'authorization');
  }

  result.headers = headers;

  return result;
}

// TODO: move these top level functions to src/lib/api and keep this file just for hooks
export async function api(
  url: string,
  user: User | null,
  init: RequestInit,
  options: ApiOptions,
) {
  const reqInit = await generateApiRequestInit(
    user,
    {
      ...init,
      ...baseRequest,
    },
    options,
  );

  if (url[0] === '/') {
    url = url.slice(1);
  }

  // TODO: retry logic on 502, 503, 504s, or Network Error / timeout
  return await fetch(`/api/${url}`, reqInit);
}

export async function authorize({
  firebaseUser,
  options,
}: {
  firebaseUser: User;
  options: ApiOptions;
}) {
  const payload: AuthorizationRequest = { uid: firebaseUser.uid };

  const result = await api(
    '/auth/authorization',
    firebaseUser,
    makePayloadRequestInit(payload),
    options,
  );

  return (await result.json()) as AuthorizationResponse;
}

export function makePayloadRequestInit<T extends object>(
  payload: T,
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE' = 'POST',
): RequestInit {
  return {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  };
}
